import { applyOpacity, periverseTheme } from '@peritasai/ui-shared';
import type { SxProps, Theme } from '@mui/material/styles';

export const pageContainerSx = {
  px: { xs: '2.5rem', md: '1.5rem' },
  py: { xs: '2rem', md: '0.5rem' },
  display: 'flex',
  justifyContent: { xs: 'center', md: 'flex-start' },
  minHeight: 'calc(100vh - 80px)',
  position: 'relative',
} satisfies SxProps<Theme>;

export const contentWrapSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: { xs: 'center', md: 'flex-start' },
  flex: 1,
  height: { xs: '100%', md: 'calc(100vh - 165px)' },
  minHeight: 0,
  marginTop: { xs: '-36px', md: '0px' },
} satisfies SxProps<Theme>;

export const scrollableAreaSx = {
  scrollbarGutter: 'stable',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: '15px',
    backgroundColor: '#CFD8DC',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
} satisfies SxProps<Theme>;

export const pageContainerVariantSx = (
  isDiscoveryStep: boolean,
  hasSelectedLocation = false
) =>
  ({
    ...pageContainerSx,
    paddingLeft: isDiscoveryStep
      ? { xs: '0px', md: '1rem', lg: '3rem', xl: '3rem' }
      : { xs: '0px', md: '1.5rem', lg: '3rem', xl: '3rem' },
    paddingRight: isDiscoveryStep
      ? { xs: '0px', md: '1rem', lg: '3rem', xl: '3rem' }
      : { xs: '0px', md: '1.5rem', lg: '3rem', xl: '3rem' },
    py: isDiscoveryStep
      ? { xs: '0px', md: '1rem', lg: '1rem' }
      : { xs: pageContainerSx.py.xs, md: '16px'},
    minHeight: isDiscoveryStep
      ? {
          xs: 'calc(100dvh - 95px)',
          md: 'calc(100vh - 150px)',
          lg: 'calc(100vh - 120px)',
        }
      : pageContainerSx.minHeight,
    justifyContent: isDiscoveryStep
      ? 'flex-start'
      : pageContainerSx.justifyContent,
    mt: hasSelectedLocation ? { xs: '0px', md: '0px', lg: '0px' } : '0px',
    alignItems: isDiscoveryStep
      ? 'stretch'
      : { xs: 'center', md: 'flex-start' },
    ...(isDiscoveryStep
      ? {
          height: {
            xs: 'calc(100dvh - 95px)',
            md: 'calc(100vh - 150px)',
            lg: 'calc(100vh - 120px)',
          },
        }
      : {}),
  } satisfies SxProps<Theme>);

export const contentWrapVariantSx = (isDiscoveryStep: boolean) =>
  ({
    ...contentWrapSx,
    px: '0px',
    marginTop: isDiscoveryStep
      ? { xs: '0px', md: '0px' }
      : contentWrapSx.marginTop,
    height: isDiscoveryStep
      ? { xs: '100%', md: 'calc(100vh - 120px)', lg: 'calc(100vh - 120px)' }
      : contentWrapSx.height,
    minHeight: isDiscoveryStep
      ? { xs: '100%', md: 'calc(100vh - 120px)', lg: 'calc(100vh - 120px)' }
      : contentWrapSx.minHeight,
    justifyContent: isDiscoveryStep
      ? 'flex-start'
      : contentWrapSx.justifyContent,
    alignItems: isDiscoveryStep
      ? 'stretch'
      : { xs: 'center', md: 'flex-start' },
  } satisfies SxProps<Theme>);

export const listRowSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  maxWidth: '100%',
  padding: '12px 12px 12px 16px',
  gap: '16px',
  borderRadius: '16px',
  border: `1px solid ${periverseTheme.palette.divider}`,
  background: periverseTheme.palette.background.default,
  cursor: 'pointer',
  transition: 'background-color 160ms ease, border-color 160ms ease',
  '&:hover': {
    background: periverseTheme.palette.action.hover,
    borderColor: periverseTheme.palette.primary.light,
  },
} satisfies SxProps<Theme>;

export const cardTitleSx = {
  color: { xs: periverseTheme.palette.text.primary, md: 'rgba(0, 0, 0, 0.87)' },
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: { xs: '20px', md: '16px' },
  fontWeight: 600,
  lineHeight: { xs: '26.68px', md: '24px' },
  letterSpacing: '0.15px',
  textAlign: { xs: 'center', md: 'left' },
} satisfies SxProps<Theme>;

export const selectHubLocationTitleSx = {
  ...cardTitleSx,
  [periverseTheme.breakpoints.up('md')]: {
    color: 'rgba(0, 0, 0, 0.87)',
    fontFeatureSettings: '"liga" off, "clig" off',
    fontFamily: '"Helvetica Neue", "Roboto", "Arial", sans-serif',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '150%',
    letterSpacing: '0.15px',
  },
} satisfies SxProps<Theme>;

export const selectButtonSx = {
  display: 'flex',
  padding: '8px 22px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '8px',
  background: periverseTheme.palette.primary.main,
  boxShadow: '0 0 0 0 rgba(0, 0, 0, 0.25)',
  color: periverseTheme.palette.primary.contrastText,
  ...periverseTheme.typography.buttonLarge,
} satisfies SxProps<Theme>;

export const locationTitleSx = {
  textAlign: { xs: 'center', md: 'left' },
  alignSelf: { xs: 'stretch', md: 'auto' },
  flex: 1,
  color: { xs: periverseTheme.palette.text.primary, md: 'rgba(0, 0, 0, 0.60)' },
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: { xs: '24px', md: '16px' },
  fontFeatureSettings: '"liga" off, "clig" off',
  fontWeight: { xs: 500, md: 400 },
  lineHeight: { xs: '29.64px', md: '24px' },
  letterSpacing: { xs: '0.25px', md: '0.15px' },
} satisfies SxProps<Theme>;

export const selectedFlowHeaderTitleStackSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: { xs: 'flex-start', md: 'center' },
  gap: '4px',
  flex: '1 1 0',
  minWidth: 0,
  minHeight: { xs: 'auto', md: '60px' },
} satisfies SxProps<Theme>;

export const selectedFlowTitleSx = {
  color: 'rgba(0, 0, 0, 0.87)',
  fontFeatureSettings: '"liga" off, "clig" off',
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '18px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '32px',
  letterSpacing: '0.15px',
} satisfies SxProps<Theme>;

export const selectedFlowLocationSx = {
  color: 'rgba(0, 0, 0, 0.60)',
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: '0.15px',
} satisfies SxProps<Theme>;

export const featurePageTitleWrapSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  pl: { xs: '1.5rem', md: '0px' },
  pr: { xs: '1.5rem', md: '0px' },
} satisfies SxProps<Theme>;

export const featurePageStandaloneTitleWrapSx = {
  ...featurePageTitleWrapSx,
  position: { xs: 'absolute', md: 'static' },
  top: { xs: '24px', md: 'auto' },
  left: { xs: '0px', md: 'auto' },
  zIndex: { xs: 1, md: 'auto' },
  justifyContent: { xs: 'flex-start', md: 'center' },
  minHeight: { xs: 'auto', md: '40px' },
  mb: { xs: '0px', md: '12px' },
} satisfies SxProps<Theme>;

export const featurePageSelectedMobileHeaderSx = {
  width: '100%',
  display: { xs: 'flex', md: 'none' },
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
  position: 'absolute',
  top: '15px',
  left: '0px',
  zIndex: 1,
  pl: '1.5rem',
  pr: '1.5rem',
} satisfies SxProps<Theme>;

export const featurePageTitleCompactWrapSx = {
  ...featurePageTitleWrapSx,
  pt: { xs: '0px', md: '16px' },
  pb: { xs: '0px', md: '12px' },
  minHeight: { xs: 'auto', md: '44px' },
} satisfies SxProps<Theme>;

export const featurePageTitleTextSx = {
  color: 'rgba(0, 0, 0, 0.87)',
  fontFeatureSettings: '"liga" off, "clig" off',
  fontFamily: 'var(--fontFamily, "Helvetica Neue")',
  fontSize: '18px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '22px',
  letterSpacing: '0.25px',
} satisfies SxProps<Theme>;

export const locationHeaderRowSx = {
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: { xs: 'center', md: 'flex-end' },
  gap: { xs: '16px', md: '4px' },
} satisfies SxProps<Theme>;

export const caseListSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: { xs: 'center', md: 'stretch' },
  gap: '16px',
  width: '100%',
} satisfies SxProps<Theme>;

export const caseListCardStackSx = (hasSelectedLocation: boolean) =>
  ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: { xs: 'center', md: 'stretch' },
    gap: hasSelectedLocation ? { xs: '32px', md: '12px', lg: '12px' } : '32px',
    pt: hasSelectedLocation ? { xs: '5px', md: '0px', lg: '0px' } : '1rem',
    width: '100%',
  } satisfies SxProps<Theme>);

export const caseListScrollSx = (
  showAll: boolean,
  listHeight: string | number | { md: string; lg: string }
) =>
  ({
    ...caseListSx,
    height: listHeight,
    overflowY: showAll ? 'scroll' : 'auto',
    overflowX: 'hidden',
    gap: { xs: '16px', md: '12px' },
    pb: showAll ? 0 : { xs: undefined, md: '12px' },
    ...scrollableAreaSx,
  } satisfies SxProps<Theme>);

export const caseListRowWithGapSx = {
  ...listRowSx,
  gap: 2,
} satisfies SxProps<Theme>;

export const caseListRowDetailsSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  flex: 1,
} satisfies SxProps<Theme>;

export const changeLocationButtonSx = {
  height: '42px',
  padding: '8px 22px',
  borderRadius: '8px',
  color: periverseTheme.palette.secondary.main,
  textTransform: 'capitalize',
  zIndex: 2,
  ...periverseTheme.typography.buttonLarge,
} satisfies SxProps<Theme>;

export const cancelSelectionButtonSx = {
  height: '44px',
  padding: '8px 22px',
  borderRadius: '8px',
  borderColor: applyOpacity(periverseTheme.palette.primary.light, 0.5),
  color: periverseTheme.palette.primary.main,
  textTransform: 'capitalize',
  zIndex: 2,
  ...periverseTheme.typography.buttonLarge,
} satisfies SxProps<Theme>;

export const caseCardWrapSx = {
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: { xs: 'center', md: 'stretch' },
  gap: '32px',
  padding: '0px',
  flex: '1 0 0',
  alignSelf: 'stretch',
  justifyContent: { xs: 'center', md: 'flex-start' },
} satisfies SxProps<Theme>;

export const selectCaseHeadingSx = {
  width: '100%',
  display: 'flex',
  justifyContent: { xs: 'center', md: 'flex-start' },
} satisfies SxProps<Theme>;

export const locationHeadingSx = {
  width: '100%',
  display: 'flex',
  justifyContent: { xs: 'center', md: 'flex-start' },
} satisfies SxProps<Theme>;

export const caseTitleSx = {
  color: 'rgba(0, 0, 0, 0.87)',
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  letterSpacing: '0.15px',
} satisfies SxProps<Theme>;

export const locationNameSx = {
  ...caseTitleSx,
  flex: '1 1 auto',
  minWidth: 0,
  overflowWrap: 'anywhere',
} satisfies SxProps<Theme>;

export const caseSubtitleSx = {
  color: periverseTheme.palette.text.secondary,
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20.02px',
  letterSpacing: '0.17px',
} satisfies SxProps<Theme>;

export const discoveryContainerSx = {
  width: '100%',
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: { xs: 'center', md: 'stretch' },
  flex: 1,
  height: '100%',
  minHeight: 0,
  alignSelf: 'stretch',
} satisfies SxProps<Theme>;

export const discoveryHeaderWrapSx = {
  width: '100%',
  maxWidth: '100%',
  px: { xs: '16px', md: '0px' },
  pt: { xs: '16px', md: '0px' },
} satisfies SxProps<Theme>;


export const discoveryHeaderSkeletonStackSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: '1 1 0',
} satisfies SxProps<Theme>;

export const discoveryHeaderSx = {
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  gap: '4px',
} satisfies SxProps<Theme>;

export const discoveryCardWrapSx = {
  width: '100%',
  maxWidth: '100%',
  flex: 1,
  height: '100%',
  minHeight: 0,
  minWidth: 0,
  px: { xs: '16px', md: '0px' },
  pt: 0,
} satisfies SxProps<Theme>;



export const discoveryActionsSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
} satisfies SxProps<Theme>;



export const discoveryCardSx = {
  width: '100%',
  maxWidth: '100%',
  alignSelf: 'stretch',
  borderRadius: '24px',
  border: `1px solid ${periverseTheme.palette.divider}`,
  background: periverseTheme.palette.background.paper,
  boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.08)',
  padding: { xs: '16px', md: '24px' },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0px',
  flex: 1,
  minHeight: 0,
  height: '100%',
} satisfies SxProps<Theme>;




















export const errorCardContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: '16px',
  width: '100%',
} satisfies SxProps<Theme>;

export const errorCardActionsSx = {
  display: 'flex',
  justifyContent: 'center',
  gap: 1,
} satisfies SxProps<Theme>;

export const loadingCardGridSx = {
  display: 'grid',
  gap: 2,
  width: '100%',
} satisfies SxProps<Theme>;

export const locationListRowContentSx = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
} satisfies SxProps<Theme>;





export const popupOverlaySx = {
  position: 'fixed',
  inset: 0,
  width: '100vw',
  height: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: { xs: '16px', md: '24px' },
  py: { xs: '24px', md: '32px' },
  backgroundColor: 'rgba(0, 0, 0, 0.20)',
  zIndex: periverseTheme.zIndex.modal,
} satisfies SxProps<Theme>;

export const caseListEmptyStateSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  padding: '32px 16px',
  textAlign: 'center',
};

export const caseListEmptyMessageSx: SxProps<Theme> = {
  color: periverseTheme.palette.text.secondary,
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.15px',
};

export const caseListEmptyIconSx: SxProps<Theme> = {
  fontSize: '40px',
  color: periverseTheme.palette.text.disabled,
};
